import React, { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { NavLink, useParams } from "react-router-dom";
import { Button, Spinner, addToast, cn } from "@heroui/react";
import { useTranslation } from "react-i18next";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

import { AiOutlineUsergroupAdd } from "react-icons/ai";
import { IoIosArrowBack } from "react-icons/io";

import UserFormAccount from "./UserFormAccount";
import UserFormProfile from "./UserFormProfile";
import UserFormCustomerList from "./UserFormCustomerList";

type UserType = {
  user_id: string;
  user_name: string;
  user_given_name: string;
  user_email: string;
  availability: string;
  allow_update_entities: boolean;
  allow_update_assignees: boolean;
  [key: string]: any;
};

type EntityType = {
  entity_id: string;
  entity_name: string;
  entity_address: string;
  entity_postalCode: string;
  entity_city: string;
  entity_email: string[];
};
interface Params {
  [key: string]: string | undefined;
  userId: string;
}

type FormValues = {
  user_name: string;
  user_given_name: string;
  availability: string;
  allow_update_entities: boolean;
  allow_update_assignees: boolean;
  user_email: string;
  entities: string[];
  user_group: string[];
};

export default function UserForm() {
  const { t } = useTranslation();
  const params = useParams<Params>();
  const axiosPrivate = useAxiosPrivate();

  const [dataUser, setDataUser] = useState<UserType | null>(null);
  const [dataEntities, setDataEntities] = useState<EntityType[]>([]);
  const [userLoading, setUserLoading] = useState(true);
  const [entitiesLoading, setEntitiesLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);

  const methods = useForm<FormValues>({
    mode: "onBlur",
  });
  const methodsCustomers = useForm({
    mode: "onBlur",
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axiosPrivate.get(`/users/${params.userId}`);
        const userData = response.data;
        setDataUser(userData);

        let entities = [];
        if (Array.isArray(userData.entities)) {
          entities = userData.entities;
        } else if (userData.entities) {
          entities = userData.entities.split(",");
        }

        let user_group = [];
        if (Array.isArray(userData.user_group)) {
          user_group = userData.user_group;
        } else if (userData.user_group) {
          user_group = userData.user_group.split(",");
        }

        setSelectedCustomers(userData.customer_group_id_list.split(","));

        methods.reset({
          ...userData,
          allow_update_entities: userData.allow_update_entities ? "true" : "false",
          allow_update_assignees: userData.allow_update_assignees ? "true" : "false",
          entities: entities,
          user_group: user_group,
        });
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des données utilisateur:",
          error,
        );
      } finally {
        setUserLoading(false);
      }
    };

    const fetchENtities = async () => {
      try {
        const responseUseCases = await axiosPrivate.get(`/useCases`);
        const entitiesData =
          responseUseCases.data["data"][0]["settings"]["entities"];
        setDataEntities(entitiesData);
      } catch (error) {
        console.error("Erreur lors de la récupération des entities:", error);
      } finally {
        setEntitiesLoading(false);
      }
    };

    fetchUser();
    fetchENtities();
  }, [params.userId, axiosPrivate, methods]);

  const onSubmit = async (formData: any) => {
    setSubmitting(true);
    const formattedData = {
      ...formData,
      allow_update_entities: formData.allow_update_entities === "true",
      allow_update_assignees: formData.allow_update_assignees === "true",
      entities: Array.isArray(formData.entities)
        ? formData.entities.join(",")
        : formData.entities,
      user_group: Array.isArray(formData.user_group)
        ? formData.user_group.join(",")
        : formData.user_group,
    };

    await axiosPrivate.put(`/users/${params.userId}`, formattedData);
    setSubmitting(false);
  };

  const updateUserInAPI = React.useCallback(
    async (updatedSelectedCustomers: string[]) => {
      try {
        const customerGroupIdList = updatedSelectedCustomers.join(",");

        await axiosPrivate.put(`/users/${params.userId}`, {
          customer_group_id_list: customerGroupIdList,
        });
        
        addToast({
          title: t("successfully_updated"),
          description: t("customer_group_updated"),
          classNames: {
            base: cn([
              "bg-vistagreen",
              "border-1",
            ]),
            title: "text-white",
            description: "text-white/70",
          },
        })
        
      } catch (error) {
        console.error(
          "Erreur lors de la mise à jour des données utilisateur:",
          error,
        );
      }
    },
    [axiosPrivate, params.userId],
  );

  const handleDeletedItems = (data: string) => {
    setSelectedCustomers((prevSelectedCustomers) => {
      const updatedSelectedCustomers = prevSelectedCustomers.filter(
        (item) => item !== data
      );
      updateUserInAPI(updatedSelectedCustomers);
      return updatedSelectedCustomers;
    });
  };

  const handleSelectedItems = React.useCallback(
    (selectedItems: string[] = []) => {
      setSelectedCustomers((prevSelectedCustomers) => {
        const updatedSelectedCustomers = [
          ...prevSelectedCustomers,
          ...selectedItems,
        ];
        updateUserInAPI(updatedSelectedCustomers);
        return updatedSelectedCustomers;
      });
    },
    [updateUserInAPI]
  );

  const classNames = React.useMemo(
    () => ({
      tabList: "gap-6 w-full relative rounded-none p-0 border-b border-divider",
      cursor: "w-full bg-vistagreen",
      tab: "max-w-fit px-0 h-12 border-vistagreen",
      tabContent: "group-data-[selected=true]:text-vistagreen",
      panel: ["w-full bg-white rounded-lg"],
      label: [
        "text-[10px] uppercase text-vistagrey/60 z-0 group-data-[filled=true]:text-vistagrey/60",
      ],
      input: [],
      inputWrapper: [""],
      mainWrapper: [],
      base: ["pb-4"],
    }),
    [],
  );

  if (userLoading || entitiesLoading) {
    return <div>{t("loading")}</div>;
  }

  return (
    <>
      <div className="w-full text-vistagrey mb-6 inline-flex">
        <div>
          <div className="text-vistagrey text-xl">
            <AiOutlineUsergroupAdd className="fill-vistagreen inline-block text-4xl mr-2" />
            {t("users")}
          </div>
          <div className="ml-10">
            <NavLink
              to={`/users/`}
            >
              <Button
                color="primary"
                variant="bordered"
                size="sm"
                radius="full"
                startContent={<IoIosArrowBack />}
              >
                {t("back")}
              </Button>
            </NavLink>
          </div>
        </div>
      </div>
      <div className="flex gap-10">
        <div className="bg-white rounded-lg w-3/12">
          <UserFormProfile
            methods={methods}
            classNames={classNames}
            dataUser={dataUser}
          />
        </div>
        <div className="bg-white rounded-lg w-9/12">
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
              className="flex flex-col items-start gap-4"
            >
              <div className="relative w-full">
                <div className="border-b-1 border-dashed border-vistagrey/30 p-3 font-bold">
                  {t("profile_editing")}
                </div>
                <div className="p-5">
                  <UserFormAccount
                    methods={methods}
                    classNames={classNames}
                    dataUser={dataUser}
                    dataEntities={dataEntities}
                  />
                  <Button
                    type="submit"
                    className="bg-vistagreen text-white rounded-md p-3"
                  >
                    {submitting && <Spinner color="default" />}
                    {!submitting && <div>{t("validate_changes")}</div>}
                  </Button>
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
      <div className="bg-white rounded-lg mt-10">
        <div className="border-b-1 border-dashed border-vistagrey/30 p-3 font-bold">
          {t("assigned_customers_group_list")}
        </div>
        <FormProvider {...methodsCustomers}>
          <form
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
            className="flex flex-col items-start gap-4"
          >
            <div className="p-4 w-full">
              <UserFormCustomerList
                currentCustomerGroup={selectedCustomers}
                onSelectedItems={handleSelectedItems}
                onDeletedItems={handleDeletedItems}
              />
            </div>
          </form>
        </FormProvider>
      </div>
    </>
  );
}
